import { useParams, useNavigate } from 'react-router-dom';
import AddOnPicker, { type AddOnSelection } from '@/components/AddOnPicker';
import { productsApi } from '@/services/products';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import SearchableSelect from '@/components/SearchableSelect';
import {
  Page,
  Layout,
  Card,
  Badge,
  BlockStack,
  InlineStack,
  Text,
  SkeletonBodyText,
  Box,
  Divider,
  DataTable,
  Button,
  Modal,
  Select,
  TextField,
  Banner,
  FormLayout,
  Checkbox,
} from '@shopify/polaris';
import { rentalsApi, extensionsApi, warehouseApi, clinicsApi, patientsApi, assetsApi } from '@/services';
import api from '@/services/api';
import type { RentalStatus, RentalItem } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

/**
 * Rentals that cannot be extended: the equipment is already back with us, or the
 * order is dead. Everything else can be extended, including before dispatch.
 *
 * Kept as a deny-list to match extensions.service.ts. The previous allow-list of
 * in_use/extended/overdue meant a patient asking for a longer rental before the
 * equipment shipped had to wait for it to ship first.
 */
const NOT_EXTENDABLE_STATUSES: string[] = [
  'returned',
  'qc_hold',
  'repair',
  'complete',
  'cancelled',
  'surgery_cancelled',
  'refunded',
];

export default function RentalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: addOnProducts } = useQuery({
    queryKey: ['addon-products'],
    queryFn: () => productsApi.addons(),
  });

  // Extension modal
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  // Contact details. The order holds its own copy of these, so correcting the order
  // and correcting the patient record are separate acts — hence the checkbox.
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactPatientPhone, setContactPatientPhone] = useState('');
  const [contactUpdateProfile, setContactUpdateProfile] = useState(true);
  const [contactError, setContactError] = useState<string | null>(null);

  // Swap a mis-picked serial before dispatch. Distinct from Replace, which is for
  // equipment already with a patient.
  const [changeSerialModalOpen, setChangeSerialModalOpen] = useState(false);
  const [changeOldSerial, setChangeOldSerial] = useState('');
  const [changeNewSerial, setChangeNewSerial] = useState('');
  const [changeSerialSku, setChangeSerialSku] = useState('');
  const [changeSerialReason, setChangeSerialReason] = useState('');
  const [changeSerialError, setChangeSerialError] = useState<string | null>(null);

  // Free-form end date, as distinct from extending by whole weeks.
  const [endDateModalOpen, setEndDateModalOpen] = useState(false);
  const [newEndDate, setNewEndDate] = useState('');
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [extendWeeks, setExtendWeeks] = useState('1');
  const [extendNotes, setExtendNotes] = useState('');

  // Cancel/Refund modals
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelNotes, setCancelNotes] = useState('');
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundNotes, setRefundNotes] = useState('');
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueNotes, setIssueNotes] = useState('');
  const [newComment, setNewComment] = useState('');
  const [commentCustomerNotified, setCommentCustomerNotified] = useState(false);
  const [replacementOrderModalOpen, setReplacementOrderModalOpen] = useState(false);
  const [replacementReason, setReplacementReason] = useState('');

  // Label generation
  const [labelError, setLabelError] = useState<string | null>(null);

  // Action feedback
  const [actionBanner, setActionBanner] = useState<{ tone: 'success' | 'critical'; message: string } | null>(null);

  // Replacement modal
  const [replaceModalOpen, setReplaceModalOpen] = useState(false);
  const [replaceItemId, setReplaceItemId] = useState('');
  const [replaceOldSerial, setReplaceOldSerial] = useState('');
  const [replaceNewSerial, setReplaceNewSerial] = useState('');
  const [replaceReason, setReplaceReason] = useState('');

  // Partial refund modal
  const [partialRefundModalOpen, setPartialRefundModalOpen] = useState(false);
  const [partialRefundAmount, setPartialRefundAmount] = useState('');
  const [partialRefundReason, setPartialRefundReason] = useState('');

  // Convert to sale modal
  const [convertSaleModalOpen, setConvertSaleModalOpen] = useState(false);
  const [salePrice, setSalePrice] = useState('');
  const [saleReason, setSaleReason] = useState('');

  // Change Address modal state
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [addItemSelections, setAddItemSelections] = useState<AddOnSelection[]>([]);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressType, setAddressType] = useState<string>('clinic');
  const [patientId, setPatientId] = useState<string>('');
  const [addressError, setAddressError] = useState<string | null>(null);
  const [customAddr, setCustomAddr] = useState({
    name: '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    province: '',
    postalCode: '',
    phone: '',
  });

  // Reassign modal state
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassignClinicId, setReassignClinicId] = useState<string>('');
  const [reassignPatientId, setReassignPatientId] = useState<string>('');
  const [reassignReason, setReassignReason] = useState<string>('');
  const [reassignError, setReassignError] = useState<string | null>(null);

  const { data: rental, isLoading } = useQuery({
    queryKey: ['rental', id],
    queryFn: () => rentalsApi.getById(id!),
    enabled: !!id,
  });

  const { data: shipments } = useQuery({
    queryKey: ['rental-shipments', id],
    queryFn: () => rentalsApi.getShipments(id!),
    enabled: !!id,
  });

  const { data: history } = useQuery({
    queryKey: ['rental-history', id],
    queryFn: () => rentalsApi.getHistory(id!),
    enabled: !!id,
  });

  const updateAddressMutation = useMutation({
    mutationFn: (dto: Parameters<typeof rentalsApi.updateAddress>[1]) =>
      rentalsApi.updateAddress(id!, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      setAddressModalOpen(false);
      setAddressError(null);
    },
    onError: (err: any) => {
      setAddressError(err?.response?.data?.message || 'Failed to update address');
    },
  });

  // Clinic lookup for the reassign modal. Searches on the server — the previous
  // capped fetch fed a native Select, which cannot be typed into, so most of the
  // 842 clinics could not be reached.
  const [reassignClinicName, setReassignClinicName] = useState('');
  const searchReassignClinics = useCallback(async (query: string) => {
    const res = await clinicsApi.list({ search: query || undefined, limit: 25, status: 'active' });
    return res.data.map((c) => ({
      label: c.name,
      value: c.id,
      detail: c.email || undefined,
    }));
  }, []);

  const { data: reassignPatientsData } = useQuery({
    queryKey: ['patients-by-clinic', reassignClinicId],
    queryFn: () =>
      patientsApi.list({
        clinicId: reassignClinicId || undefined,
        limit: 200,
      }),
    enabled: reassignModalOpen && !!reassignClinicId,
  });

  const reassignMutation = useMutation({
    mutationFn: (dto: { clinicId?: string | null; patientId?: string | null; reason?: string }) =>
      rentalsApi.reassign(id!, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setReassignModalOpen(false);
      setReassignReason('');
      setReassignError(null);
      setActionBanner({ tone: 'success', message: 'Rental reassigned' });
    },
    onError: (err: any) => {
      setReassignError(err?.response?.data?.message || 'Failed to reassign rental');
    },
  });

  // Extension pricing preview
  const { data: extensionPreview, isFetching: extensionPricingLoading } = useQuery({
    queryKey: ['extension-preview', id, extendWeeks],
    queryFn: () => extensionsApi.calculatePrice(id!, parseInt(extendWeeks, 10)),
    enabled: extendModalOpen && !!id && parseInt(extendWeeks, 10) > 0,
  });

  // Extension history
  const { data: extensionHistory } = useQuery({
    queryKey: ['extension-history', id],
    queryFn: () => extensionsApi.getHistory(id!),
    enabled: !!id,
  });

  // Extend mutation
  const contactMutation = useMutation({
    mutationFn: () =>
      rentalsApi.updateContact(id!, {
        customerEmail: contactEmail,
        customerPhone: contactPhone,
        patientPhone: contactPatientPhone,
        updatePatientProfile: contactUpdateProfile,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setContactModalOpen(false);
      setContactError(null);
      setActionBanner({ tone: 'success', message: 'Contact details updated' });
    },
    onError: (err: any) => {
      setContactError(err?.response?.data?.message || 'Failed to update contact details');
    },
  });

  const changeSerialMutation = useMutation({
    mutationFn: () =>
      warehouseApi.changeSerial(id!, {
        oldSerial: changeOldSerial,
        newSerial: changeNewSerial,
        reason: changeSerialReason || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setChangeSerialModalOpen(false);
      setChangeNewSerial('');
      setChangeSerialReason('');
      setChangeSerialError(null);
      setActionBanner({ tone: 'success', message: 'Serial changed' });
    },
    onError: (err: any) => {
      setChangeSerialError(err?.response?.data?.message || 'Failed to change the serial');
    },
  });

  const searchReplacementSerials = useCallback(
    async (query: string) => {
      if (!changeSerialSku) return [];
      const res = await assetsApi.searchAvailable(changeSerialSku, query || undefined, 25);
      return res.data.map((a) => ({
        label: a.serialNumber,
        value: a.serialNumber,
        detail: a.productName || undefined,
      }));
    },
    [changeSerialSku],
  );

  const endDateMutation = useMutation({
    mutationFn: (date: string) => rentalsApi.updateEndDate(id!, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setEndDateModalOpen(false);
      setEndDateError(null);
      setActionBanner({ tone: 'success', message: 'End date updated' });
    },
    onError: (err: any) => {
      setEndDateError(err?.response?.data?.message || 'Failed to update the end date');
    },
  });

  const extendMutation = useMutation({
    mutationFn: (dto: { rentalId: string; weeks: number; notes?: string }) =>
      extensionsApi.extend({ rentalId: dto.rentalId, weeks: dto.weeks, notes: dto.notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['extension-history', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setExtendModalOpen(false);
      setExtendWeeks('1');
      setExtendNotes('');
      setActionBanner({ tone: 'success', message: 'Rental extended successfully' });
    },
    onError: (err: any) => {
      setActionBanner({ tone: 'critical', message: err?.response?.data?.message || 'Failed to extend rental' });
    },
  });

  // Adding an item is only offered before dispatch; the server enforces the same
  // rule, so a stale page cannot slip an item onto an order that has already gone.
  const addItemsMutation = useMutation({
    mutationFn: () =>
      rentalsApi.addItems(
        id!,
        addItemSelections.map((sel) => ({
          productSku: sel.sku,
          productName: sel.name,
          quantity: sel.quantity,
          unitPrice: sel.unitPrice,
          itemType: 'addon',
          variantId: sel.variantId,
        })),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setAddItemModalOpen(false);
      setAddItemSelections([]);
      setActionBanner({
        tone: 'success',
        message: 'Item added and order repriced. The shipping label is unchanged.',
      });
    },
    onError: (err: any) => {
      setActionBanner({
        tone: 'critical',
        message: err?.response?.data?.message || 'Failed to add item',
      });
    },
  });

  // Cancel mutation (uses transition endpoint)
  const cancelMutation = useMutation({
    mutationFn: () => rentalsApi.cancel(id!, cancelNotes || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setCancelModalOpen(false);
      setCancelNotes('');
      setActionBanner({ tone: 'success', message: 'Order cancelled' });
    },
    onError: (err: any) => {
      setActionBanner({ tone: 'critical', message: err?.response?.data?.message || 'Failed to cancel order' });
    },
  });

  const replacementOrderMutation = useMutation({
    mutationFn: () => rentalsApi.createReplacement(id!, replacementReason),
    onSuccess: (data) => {
      setReplacementOrderModalOpen(false);
      setReplacementReason('');
      // Straight to the new $0 order — it's now in the dispatch queue
      navigate(`/rentals/${data.replacement.id}`);
    },
    onError: (err: any) => {
      setActionBanner({ tone: 'critical', message: err?.response?.data?.message || 'Failed to create replacement order' });
      setReplacementOrderModalOpen(false);
    },
  });

  const commentMutation = useMutation({
    mutationFn: () => rentalsApi.addComment(id!, newComment, commentCustomerNotified),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setNewComment('');
      setCommentCustomerNotified(false);
    },
    onError: (err: any) => {
      setActionBanner({ tone: 'critical', message: err?.response?.data?.message || 'Failed to add comment' });
    },
  });

  // Issue report/resolve mutations
  const issueMutation = useMutation({
    mutationFn: () => rentalsApi.reportIssue(id!, issueNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setIssueModalOpen(false);
      setIssueNotes('');
      setActionBanner({ tone: 'success', message: 'Issue flagged — QC will be warned when this unit is scanned back' });
    },
    onError: (err: any) => {
      setActionBanner({ tone: 'critical', message: err?.response?.data?.message || 'Failed to report issue' });
    },
  });

  const resolveIssueMutation = useMutation({
    mutationFn: () => rentalsApi.resolveIssue(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setActionBanner({ tone: 'success', message: 'Issue cleared' });
    },
  });

  // "Not a duplicate": releases the dispatch hold on a possible-duplicate order.
  const clearDuplicateMutation = useMutation({
    mutationFn: () => rentalsApi.clearDuplicate(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      queryClient.invalidateQueries({ queryKey: ['rentals'] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-queue'] });
      setActionBanner({ tone: 'success', message: 'Duplicate check cleared — released for dispatch' });
    },
    onError: (err: any) => {
      setActionBanner({ tone: 'critical', message: err?.response?.data?.message || 'Failed to clear duplicate flag' });
    },
  });

  // Refund mutation
  const refundMutation = useMutation({
    mutationFn: () => rentalsApi.refund(id!, refundNotes || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setRefundModalOpen(false);
      setRefundNotes('');
      setActionBanner({ tone: 'success', message: 'Refund processed' });
    },
    onError: (err: any) => {
      setActionBanner({ tone: 'critical', message: err?.response?.data?.message || 'Failed to process refund' });
    },
  });

  // Generate label mutation
  const generateLabelMutation = useMutation({
    mutationFn: () => warehouseApi.generateLabel(id!),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      const outboundTrack = data.outbound.trackingNumber;
      const returnTrack = data.returnLabel?.trackingNumber;
      const msg = returnTrack
        ? `Outbound: ${outboundTrack} • Return: ${returnTrack}`
        : data.returnLabelError
          ? `Outbound: ${outboundTrack} (return label failed — ${data.returnLabelError})`
          : `Outbound: ${outboundTrack}`;
      setActionBanner({ tone: 'success', message: `Labels generated. ${msg}` });
    },
    onError: (err: any) => {
      setLabelError(err?.response?.data?.message || 'Failed to generate label');
    },
  });

  const partialRefundMutation = useMutation({
    mutationFn: () => api.post(`/rentals/${id}/partial-refund`, {
      amount: parseFloat(partialRefundAmount),
      reason: partialRefundReason,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setPartialRefundModalOpen(false);
      setPartialRefundAmount('');
      setPartialRefundReason('');
      setActionBanner({ tone: 'success', message: `Partial refund of $${parseFloat(partialRefundAmount).toFixed(2)} processed` });
    },
    onError: (err: any) => {
      setActionBanner({ tone: 'critical', message: err?.response?.data?.message || 'Failed to process partial refund' });
    },
  });

  const convertSaleMutation = useMutation({
    mutationFn: () => api.post(`/rentals/${id}/convert-to-sale`, {
      salePrice: parseFloat(salePrice),
      reason: saleReason || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setConvertSaleModalOpen(false);
      setSalePrice('');
      setSaleReason('');
      setActionBanner({ tone: 'success', message: 'Rental converted to sale successfully' });
    },
    onError: (err: any) => {
      setActionBanner({ tone: 'critical', message: err?.response?.data?.message || 'Failed to convert to sale' });
    },
  });

  const replaceMutation = useMutation({
    mutationFn: (dto: { rentalItemId: string; oldSerialNumber: string; newSerialNumber: string; reason?: string }) =>
      api.post(`/rentals/${id}/replace`, dto).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
      queryClient.invalidateQueries({ queryKey: ['rental-history', id] });
      setReplaceModalOpen(false);
      setReplaceNewSerial('');
      setReplaceReason('');
      setActionBanner({ tone: 'success', message: 'Equipment replaced successfully' });
    },
    onError: (err: any) => {
      setActionBanner({ tone: 'critical', message: err?.response?.data?.message || 'Failed to replace equipment' });
    },
  });

  const handleOpenAddressModal = () => {
    if (rental) {
      setAddressType(rental.shippingAddressType || 'clinic');
      setPatientId((rental as any).patientId || '');
      const existing = (rental as any).customShippingAddress;
      setCustomAddr({
        name: existing?.name || '',
        company: existing?.company || '',
        address1: existing?.address1 || '',
        address2: existing?.address2 || '',
        city: existing?.city || '',
        province: existing?.province || '',
        postalCode: existing?.postalCode || '',
        phone: existing?.phone || '',
      });
    }
    setAddressError(null);
    setAddressModalOpen(true);
  };

  const handleSaveAddress = () => {
    if (addressType === 'custom') {
      if (!customAddr.address1.trim() || !customAddr.city.trim() || !customAddr.postalCode.trim()) {
        setAddressError('Street address, city, and postal code are required for a custom address');
        return;
      }
    }
    updateAddressMutation.mutate({
      shippingAddressType: addressType,
      patientId: addressType === 'patient' ? patientId || undefined : undefined,
      customAddress:
        addressType === 'custom'
          ? {
              name: customAddr.name || undefined,
              company: customAddr.company || undefined,
              address1: customAddr.address1,
              address2: customAddr.address2 || undefined,
              city: customAddr.city,
              province: customAddr.province,
              postalCode: customAddr.postalCode,
              country: 'CA',
              phone: customAddr.phone || undefined,
            }
          : undefined,
    });
  };

  const handleOpenReassignModal = () => {
    if (rental) {
      setReassignClinicId((rental as any).clinicId || '');
      setReassignPatientId((rental as any).patientId || '');
    }
    setReassignReason('');
    setReassignError(null);
    setReassignModalOpen(true);
  };

  const handleSaveReassign = () => {
    reassignMutation.mutate({
      clinicId: reassignClinicId || null,
      patientId: reassignPatientId || null,
      reason: reassignReason || undefined,
    });
  };

  const getStatusBadge = (status: RentalStatus) => {
    const statusConfig: Record<RentalStatus, { tone: 'success' | 'warning' | 'critical' | 'info' | undefined; label: string }> = {
      pending: { tone: undefined, label: 'Pending' },
      processing: { tone: 'info', label: 'Processing' },
      ready_to_ship: { tone: 'info', label: 'Ready to Ship' },
      shipped: { tone: 'info', label: 'Shipped' },
      in_use: { tone: 'success', label: 'In Use' },
      extended: { tone: 'warning', label: 'Extended' },
      returned: { tone: 'warning', label: 'Returned' },
      complete: { tone: 'success', label: 'Complete' },
      cancelled: { tone: 'critical', label: 'Cancelled' },
      refunded: { tone: 'critical', label: 'Refunded' },
      partial_refund: { tone: 'warning', label: 'Partial Refund' },
      overdue: { tone: 'critical', label: 'Overdue' },
      fulfilled_by_clinic: { tone: 'success', label: 'Fulfilled by Clinic' },
      surgery_cancelled: { tone: 'critical', label: 'Surgery Cancelled' },
    };
    const config = statusConfig[status] || { tone: undefined, label: status };
    return <Badge tone={config.tone}>{config.label}</Badge>;
  };

  if (isLoading) {
    return (
      <Page title="Rental Details">
        <Card>
          <SkeletonBodyText lines={10} />
        </Card>
      </Page>
    );
  }

  if (!rental) {
    return (
      <Page
        title="Rental Not Found"
        backAction={{ content: 'Back', onAction: () => navigate('/rentals') }}
      >
        <Card>
          <Text as="p">The requested rental could not be found.</Text>
        </Card>
      </Page>
    );
  }

  // Shopify numbers already carry their "#"; admin (ADM-…) ones do not.
  const duplicateOf = rental.duplicateOfOrderNumber;
  const duplicateLabel = duplicateOf
    ? duplicateOf.startsWith('#') ? duplicateOf : `#${duplicateOf}`
    : 'on file';

  // Only equipment lines map 1:1 to physical assets and carry serials; wrap,
  // bilateral-fee, and add-on lines are shown separately as accessories.
  const isEquipment = (item: RentalItem) =>
    (item.itemType ?? 'equipment') === 'equipment';
  const equipmentItems = rental.items.filter(isEquipment);
  const addonItems = rental.items.filter((item: RentalItem) => !isEquipment(item));

  const addonTypeLabel: Record<string, string> = {
    wrap: 'Wrap',
    bilateral_fee: 'Bilateral fee',
    addon: 'Add-on',
  };

  const itemRows = equipmentItems.map((item: RentalItem) => [
    item.productName,
    item.productSku,
    item.quantity,
    item.isBilateral ? 'Yes' : 'No',
    `$${Number(item.weeklyRate).toFixed(2)}/week`,
    item.assignedSerials.length > 0 ? (
      <BlockStack key={item.id} gap="100">
        {item.assignedSerials.map((serial) => (
          <InlineStack key={serial} gap="100" blockAlign="center">
            <Badge tone="info">{serial}</Badge>
            {['in_use', 'extended', 'overdue'].includes(rental.rentalStatus) && (
              <Button
                size="slim"
                variant="plain"
                tone="critical"
                onClick={() => {
                  setReplaceItemId(item.id);
                  setReplaceOldSerial(serial);
                  setReplaceModalOpen(true);
                }}
              >
                Replace
              </Button>
            )}
            {/* Before dispatch the unit is still on our shelf, so a mis-pick is just
                corrected. After dispatch it is with a patient and needs Replace,
                which keeps the original unit's history. */}
            {['pending', 'processing', 'ready_to_ship'].includes(rental.rentalStatus) && (
              <Button
                size="slim"
                variant="plain"
                onClick={() => {
                  setChangeOldSerial(serial);
                  setChangeSerialSku(item.productSku);
                  setChangeNewSerial('');
                  setChangeSerialReason('');
                  setChangeSerialError(null);
                  setChangeSerialModalOpen(true);
                }}
              >
                Change
              </Button>
            )}
          </InlineStack>
        ))}
      </BlockStack>
    ) : (
      <Text key={item.id} as="span" tone="subdued">Not assigned</Text>
    ),
  ]);

  const addonRows = addonItems.map((item: RentalItem) => [
    <InlineStack key={item.id} gap="200" blockAlign="center">
      <Text as="span">{item.productName}</Text>
      <Badge tone="enabled">{addonTypeLabel[item.itemType] ?? 'Add-on'}</Badge>
    </InlineStack>,
    item.productSku,
    item.quantity,
    Number(item.totalPrice ?? 0) > 0
      ? `$${Number(item.totalPrice).toFixed(2)}`
      : '—',
  ]);

  const comments = (history || []).filter((e: any) => e.eventType === 'comment');

  const historyRows = (history || []).map((entry: { id: string; eventType: string; oldValue: string | null; newValue: string | null; performedBy: string | null; trigger: string | null; notes: string | null; createdAt: string }) => [
    new Date(entry.createdAt).toLocaleString(),
    entry.eventType,
    entry.oldValue || '-',
    entry.newValue || '-',
    entry.notes || '-',
    entry.performedBy || entry.trigger || 'System',
  ]);

  return (
    <Page
      title={`Order ${orderNumber(rental)}`}
      backAction={{ content: 'Back', onAction: () => navigate('/rentals') }}
      titleMetadata={getStatusBadge(rental.rentalStatus)}
      secondaryActions={[
        {
          content: 'Process Dispatch',
          onAction: () => navigate(`/warehouse/dispatch/${rental.id}`),
          disabled: !['pending', 'processing', 'ready_to_ship'].includes(rental.rentalStatus),
        },
        {
          content: 'Edit Contact Details',
          onAction: () => {
            setContactEmail(rental.customerEmail || '');
            setContactPhone(rental.customerPhone || '');
            setContactPatientPhone(rental.patientPhone || '');
            // Default to correcting the patient record too: a wrong number is
            // usually wrong everywhere, and the alternative silently leaves the
            // profile stale for the next order.
            setContactUpdateProfile(true);
            setContactError(null);
            setContactModalOpen(true);
          },
        },
        {
          content: 'Change End Date',
          onAction: () => {
            // Seed with the current end date so the picker opens on the right month
            // rather than on today.
            setNewEndDate(
              rental.rentalEndDate
                ? new Date(rental.rentalEndDate).toISOString().slice(0, 10)
                : '',
            );
            setEndDateError(null);
            setEndDateModalOpen(true);
          },
          disabled: NOT_EXTENDABLE_STATUSES.includes(rental.rentalStatus),
        },
        {
          content: 'Extend Rental',
          onAction: () => setExtendModalOpen(true),
          // Mirrors the server rule in extensions.service.ts: refuse only when the
          // equipment is already back, or the order is dead. Extending before dispatch
          // is allowed — a patient asking for longer before it ships is the ordinary
          // case, and the old allow-list made the team ship the order first.
          disabled: NOT_EXTENDABLE_STATUSES.includes(rental.rentalStatus),
        },
        {
          content: 'Copy Order',
          onAction: () => navigate(`/rentals/new?copyFrom=${rental.id}`),
        },
        {
          content: 'Create Replacement Order',
          onAction: () => setReplacementOrderModalOpen(true),
          disabled: !['shipped', 'in_use', 'extended', 'overdue'].includes(rental.rentalStatus),
        },
        {
          content: (rental as any).issueReportedAt ? 'Issue Reported ✓' : 'Report Issue',
          onAction: () => setIssueModalOpen(true),
          disabled:
            !!(rental as any).issueReportedAt ||
            !['shipped', 'in_use', 'extended', 'overdue'].includes(rental.rentalStatus),
        },
        {
          content: 'Reassign Clinic / Patient',
          onAction: handleOpenReassignModal,
          disabled: ['shipped', 'in_use', 'extended', 'returned', 'complete', 'cancelled', 'refunded'].includes(rental.rentalStatus),
        },
        {
          content: 'Cancel Order',
          onAction: () => setCancelModalOpen(true),
          // admin_cancel is only valid from these states in the state machine
          disabled: !['pending', 'processing', 'ready_to_ship'].includes(rental.rentalStatus),
          destructive: true,
        },
        {
          content: 'Refund',
          onAction: () => setRefundModalOpen(true),
          // refund_processed is only valid from these states in the state machine
          disabled: !['complete', 'cancelled'].includes(rental.rentalStatus),
          destructive: true,
        },
        {
          content: 'Partial Refund',
          onAction: () => setPartialRefundModalOpen(true),
          disabled: !['in_use', 'extended', 'overdue', 'complete', 'returned'].includes(rental.rentalStatus),
        },
        {
          content: 'Convert to Sale',
          onAction: () => {
            setSalePrice(String(Number(rental.totalAmount).toFixed(2)));
            setConvertSaleModalOpen(true);
          },
          disabled: !['in_use', 'extended', 'overdue'].includes(rental.rentalStatus),
        },
        {
          content: 'Generate Return Label',
          onAction: () => {
            api.post(`/shipping/label/return`, { rentalId: id }).then(() => {
              queryClient.invalidateQueries({ queryKey: ['rental', id] });
              setActionBanner({ tone: 'success', message: 'Return label generated' });
            }).catch((err: any) => {
              setActionBanner({ tone: 'critical', message: err?.response?.data?.message || 'Failed to generate return label' });
            });
          },
          disabled: !['shipped', 'in_use', 'extended', 'overdue'].includes(rental.rentalStatus) || !!rental.returnLabelUrl,
        },
      ]}
    >
      <Layout>
        {actionBanner && (
          <Layout.Section>
            <Banner tone={actionBanner.tone} onDismiss={() => setActionBanner(null)}>
              <p>{actionBanner.message}</p>
            </Banner>
          </Layout.Section>
        )}
        {rental.duplicateOfRentalId && !rental.duplicateClearedAt && (
          <Layout.Section>
            <Banner
              tone="warning"
              title={`Possible duplicate of order ${duplicateLabel}`}
              action={{
                content: 'Not a duplicate — release for dispatch',
                onAction: () => clearDuplicateMutation.mutate(),
                loading: clearDuplicateMutation.isPending,
              }}
              secondaryAction={
                ['pending', 'processing', 'ready_to_ship'].includes(rental.rentalStatus)
                  ? { content: 'Cancel this order', onAction: () => setCancelModalOpen(true) }
                  : undefined
              }
            >
              <BlockStack gap="200">
                <p>
                  This patient already has an open order for the same equipment. Dispatch is held until you
                  confirm it is not a duplicate or cancel it.
                </p>
                <InlineStack>
                  <Button variant="plain" onClick={() => navigate(`/rentals/${rental.duplicateOfRentalId}`)}>
                    {`View order ${duplicateLabel}`}
                  </Button>
                </InlineStack>
              </BlockStack>
            </Banner>
          </Layout.Section>
        )}
        {rental.duplicateOfRentalId && rental.duplicateClearedAt && (
          <Layout.Section>
            <Text as="p" tone="subdued">
              Duplicate check cleared by {rental.duplicateClearedBy || 'an admin'} on{' '}
              {new Date(rental.duplicateClearedAt).toLocaleDateString()}
            </Text>
          </Layout.Section>
        )}
        {(rental as any).issueReportedAt && (
          <Layout.Section>
            <Banner
              tone="warning"
              title={`Issue reported ${new Date((rental as any).issueReportedAt).toLocaleDateString()}`}
              action={{ content: 'Mark resolved', onAction: () => resolveIssueMutation.mutate() }}
            >
              <p>
                {(rental as any).issueNotes} — test this equipment when it is returned.
              </p>
            </Banner>
          </Layout.Section>
        )}
        {/* Order Info */}
        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Order Information</Text>
                <Divider />
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Order Number</Text>
                    <Text as="span" fontWeight="semibold">
                      {orderNumber(rental)}
                    </Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Customer</Text>
                    <Text as="span">
                      {rental.clinic ? (
                        <Button variant="plain" onClick={() => navigate(`/clinics/${rental.clinic!.id}`)}>
                          {rental.clinic.name}
                        </Button>
                      ) : (
                        rental.patientName || 'N/A'
                      )}
                    </Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Billing Type</Text>
                    <Text as="span">
                      {rental.billingType === 'clinic_invoice' ? 'Clinic Invoice' : 'Immediate'}
                    </Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Created</Text>
                    <Text as="span">{new Date(rental.createdAt).toLocaleDateString()}</Text>
                  </InlineStack>
                  {(rental as any).replacementForRentalId && (
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">Type</Text>
                      <Button
                        variant="plain"
                        onClick={() => navigate(`/rentals/${(rental as any).replacementForRentalId}`)}
                      >
                        Replacement — view original order
                      </Button>
                    </InlineStack>
                  )}
                  {(rental as any).customerName && (
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">Patient</Text>
                      <Text as="span">{(rental as any).customerName}</Text>
                    </InlineStack>
                  )}
                  {(rental as any).customerEmail && (
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">Email</Text>
                      <Text as="span">{(rental as any).customerEmail}</Text>
                    </InlineStack>
                  )}
                  {(rental as any).customerPhone && (
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">Phone</Text>
                      <Text as="span">{(rental as any).customerPhone}</Text>
                    </InlineStack>
                  )}
                  {rental.notes && (
                    <BlockStack gap="100">
                      <Text as="span" tone="subdued">Order Notes</Text>
                      <Text as="p">{rental.notes}</Text>
                    </BlockStack>
                  )}
                </BlockStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Rental Period</Text>
                <Divider />
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Start Date</Text>
                    <Text as="span">
                      {rental.rentalStartDate
                        ? new Date(rental.rentalStartDate).toLocaleDateString()
                        : 'Not set'}
                    </Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">End Date</Text>
                    <Text as="span">
                      {rental.rentalEndDate
                        ? new Date(rental.rentalEndDate).toLocaleDateString()
                        : 'Not set'}
                    </Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Duration</Text>
                    <Text as="span">{rental.rentalWeeks} weeks</Text>
                  </InlineStack>
                </BlockStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Payment</Text>
                <Divider />
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Subtotal</Text>
                    <Text as="span">${Number(rental.subtotal).toFixed(2)}</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Tax</Text>
                    <Text as="span">${Number(rental.taxAmount).toFixed(2)}</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Shipping</Text>
                    <Text as="span">${Number(rental.shippingAmount).toFixed(2)}</Text>
                  </InlineStack>
                  <Divider />
                  <InlineStack align="space-between">
                    <Text as="span" fontWeight="semibold">Total</Text>
                    <Text as="span" fontWeight="semibold">${Number(rental.totalAmount).toFixed(2)}</Text>
                  </InlineStack>
                </BlockStack>
              </BlockStack>
            </Card>

            {/* Ship-to address type */}
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingMd">Shipping</Text>
                  {['pending', 'processing', 'ready_to_ship'].includes(rental.rentalStatus) && (
                    <Button variant="plain" onClick={handleOpenAddressModal}>
                      Change Address
                    </Button>
                  )}
                </InlineStack>
                <Divider />
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Ship To</Text>
                    <Text as="span">
                      {rental.shippingAddressType === 'patient'
                        ? 'Patient'
                        : rental.shippingAddressType === 'custom'
                          ? 'Custom Address'
                          : 'Clinic'}
                    </Text>
                  </InlineStack>
                  {rental.shippingAddressType === 'custom' &&
                    (rental as any).customShippingAddress && (
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Address</Text>
                        <Text as="span">
                          {[
                            (rental as any).customShippingAddress.company,
                            (rental as any).customShippingAddress.address1,
                            (rental as any).customShippingAddress.city,
                            (rental as any).customShippingAddress.postalCode,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </Text>
                      </InlineStack>
                    )}
                  {rental.shippingTrackingNumber && (
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">Tracking</Text>
                      <Text as="span">{rental.shippingTrackingNumber}</Text>
                    </InlineStack>
                  )}
                  {rental.shippingLabelUrl && (
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">Outbound Label</Text>
                      <Button variant="plain" url={rental.shippingLabelUrl} download="outbound-label.png" target="_blank">
                        Download
                      </Button>
                    </InlineStack>
                  )}
                  {rental.returnLabelUrl && (
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">Return Label</Text>
                      <Button variant="plain" url={rental.returnLabelUrl} download="return-label.png" target="_blank">
                        Download
                      </Button>
                    </InlineStack>
                  )}
                  {!rental.shippingLabelUrl && ['processing', 'ready_to_ship'].includes(rental.rentalStatus) && (
                    <Box paddingBlockStart="200">
                      <Button
                        onClick={() => generateLabelMutation.mutate()}
                        loading={generateLabelMutation.isPending}
                        variant="primary"
                        size="slim"
                      >
                        Generate Shipping Label
                      </Button>
                      {labelError && (
                        <Box paddingBlockStart="200">
                          <Banner tone="critical" onDismiss={() => setLabelError(null)}>
                            <p>{labelError}</p>
                          </Banner>
                        </Box>
                      )}
                    </Box>
                  )}
                  {(shipments?.length ?? 0) > 0 && (
                    <BlockStack gap="200">
                      <Divider />
                      <Text as="h3" variant="headingSm">All shipments</Text>
                      {(shipments as any[]).map((s) => (
                        <InlineStack key={s.id} align="space-between" blockAlign="center" wrap>
                          <BlockStack gap="050">
                            <Text as="span">
                              <Badge tone={s.direction === 'return' ? 'info' : 'success'}>
                                {s.direction === 'return' ? 'Return' : 'Outbound'}
                              </Badge>{' '}
                              {s.trackingNumber}
                            </Text>
                            <Text as="span" tone="subdued" variant="bodySm">
                              {s.carrier}
                              {s.shipmentCost != null ? ` · $${Number(s.shipmentCost).toFixed(2)}` : ''}
                              {' · '}
                              {new Date(s.createdAt).toLocaleDateString()}
                            </Text>
                          </BlockStack>
                          {s.hasLabel && (
                            <Button
                              variant="plain"
                              onClick={async () => {
                                // Authenticated fetch — a plain link wouldn't
                                // carry the session token.
                                const resp = await api.get(
                                  `/shipping/label/${s.id}/download`,
                                  { responseType: 'blob' },
                                );
                                window.open(URL.createObjectURL(resp.data as Blob), '_blank');
                              }}
                            >
                              Label
                            </Button>
                          )}
                        </InlineStack>
                      ))}
                    </BlockStack>
                  )}
                </BlockStack>
              </BlockStack>
            </Card>

            {!rental.shippingAddress && (
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Shipping Address</Text>
                  <Divider />
                  {(() => {
                    // Resolve like the label generator: custom → patient → clinic
                    const r = rental as any;
                    const custom = r.shippingAddressType === 'custom' && r.customShippingAddress;
                    const p = r.patient;
                    const c = r.clinic?.shippingAddress;
                    if (custom) {
                      return (
                        <BlockStack gap="100">
                          {custom.name && <Text as="p">{custom.name}</Text>}
                          <Text as="p">{custom.address1 ?? custom.street}</Text>
                          <Text as="p">{custom.city}, {custom.province} {custom.postalCode}</Text>
                        </BlockStack>
                      );
                    }
                    if (r.shippingAddressType !== 'clinic' && p?.streetAddress) {
                      return (
                        <BlockStack gap="100">
                          <Text as="p">{p.firstName} {p.lastName} (patient)</Text>
                          <Text as="p">{p.streetAddress}</Text>
                          <Text as="p">{p.city}, {p.province} {p.postalCode}</Text>
                        </BlockStack>
                      );
                    }
                    if (c) {
                      return (
                        <BlockStack gap="100">
                          <Text as="p">{r.clinic.name} (clinic)</Text>
                          <Text as="p">{c.address1}</Text>
                          <Text as="p">{c.city}, {c.province ?? c.provinceCode} {c.zip}</Text>
                        </BlockStack>
                      );
                    }
                    return (
                      <Text as="p" tone="critical">
                        No shipping address resolvable — set one via Change Address before dispatch.
                      </Text>
                    );
                  })()}
                  <Button size="slim" onClick={() => setAddressModalOpen(true)}>
                    Change Address
                  </Button>
                </BlockStack>
              </Card>
            )}

            {rental.shippingAddress && (
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Shipping Address</Text>
                  <Divider />
                  <BlockStack gap="100">
                    {rental.shippingAddress.firstName && (
                      <Text as="p">
                        {rental.shippingAddress.firstName} {rental.shippingAddress.lastName}
                      </Text>
                    )}
                    {rental.shippingAddress.company && (
                      <Text as="p">{rental.shippingAddress.company}</Text>
                    )}
                    <Text as="p">{rental.shippingAddress.address1}</Text>
                    {rental.shippingAddress.address2 && (
                      <Text as="p">{rental.shippingAddress.address2}</Text>
                    )}
                    <Text as="p">
                      {rental.shippingAddress.city}, {rental.shippingAddress.province}{' '}
                      {rental.shippingAddress.zip}
                    </Text>
                    <Text as="p">{rental.shippingAddress.country}</Text>
                  </BlockStack>
                  {rental.shippingTrackingNumber && (
                    <>
                      <Divider />
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Tracking</Text>
                        <Text as="span">{rental.shippingTrackingNumber}</Text>
                      </InlineStack>
                    </>
                  )}
                </BlockStack>
              </Card>
            )}
            {/* Replenishment cross-link */}
            {(rental as any).isReplenishment && (rental as any).replenishmentForRental && (
              <Card>
                <BlockStack gap="300">
                  <Banner tone="info">
                    <p>
                      This is a replenishment order for rental{' '}
                      <Button
                        variant="plain"
                        onClick={() => navigate(`/rentals/${(rental as any).replenishmentForRental.id}`)}
                      >
                        {orderNumber((rental as any).replenishmentForRental)}
                      </Button>
                    </p>
                  </Banner>
                </BlockStack>
              </Card>
            )}
            {!(rental as any).isReplenishment && (rental as any).replenishmentRental && (
              <Card>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">Replenishment Order</Text>
                  <Divider />
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Replenishment</Text>
                    <Button
                      variant="plain"
                      onClick={() => navigate(`/rentals/${(rental as any).replenishmentRental.id}`)}
                    >
                      {orderNumber((rental as any).replenishmentRental)}
                    </Button>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Status</Text>
                    {getStatusBadge((rental as any).replenishmentRental.rentalStatus)}
                  </InlineStack>
                </BlockStack>
              </Card>
            )}
          </BlockStack>
        </Layout.Section>

        {/* Items & History */}
        <Layout.Section>
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Equipment</Text>
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'text']}
                  headings={['Product', 'SKU', 'Qty', 'Bilateral', 'Rate', 'Serials']}
                  rows={itemRows}
                />
              </BlockStack>
            </Card>

            {(addonRows.length > 0 || ['pending', 'processing', 'ready_to_ship'].includes(rental.rentalStatus)) && (
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingMd">Add-ons &amp; Accessories</Text>
                    {['pending', 'processing', 'ready_to_ship'].includes(rental.rentalStatus) && (
                      <Button size="slim" onClick={() => setAddItemModalOpen(true)}>
                        Add item
                      </Button>
                    )}
                  </InlineStack>
                  {addonRows.length > 0 ? (
                    <DataTable
                      columnContentTypes={['text', 'text', 'numeric', 'numeric']}
                      headings={['Item', 'SKU', 'Qty', 'Price']}
                      rows={addonRows}
                    />
                  ) : (
                    <Text as="p" tone="subdued">No add-ons on this order.</Text>
                  )}
                </BlockStack>
              </Card>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Comments</Text>
                {comments.length === 0 ? (
                  <Text as="p" tone="subdued">No comments yet</Text>
                ) : (
                  <BlockStack gap="300">
                    {comments.map((c: any) => (
                      <BlockStack key={c.id} gap="050">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="span" fontWeight="semibold">{c.performedBy || 'staff'}</Text>
                          <Text as="span" tone="subdued" variant="bodySm">
                            {new Date(c.createdAt).toLocaleString()}
                          </Text>
                          {c.newValue === 'customer_notified' && (
                            <Badge tone="info">Customer notified</Badge>
                          )}
                        </InlineStack>
                        <Text as="p">{c.notes}</Text>
                      </BlockStack>
                    ))}
                  </BlockStack>
                )}
                <Divider />
                <TextField
                  label="Add a comment"
                  labelHidden
                  placeholder="Add a comment…"
                  value={newComment}
                  onChange={setNewComment}
                  multiline={2}
                  autoComplete="off"
                />
                <InlineStack align="space-between" blockAlign="center">
                  <Checkbox
                    label="Customer was notified"
                    checked={commentCustomerNotified}
                    onChange={setCommentCustomerNotified}
                  />
                  <Button
                    variant="primary"
                    size="slim"
                    disabled={!newComment.trim()}
                    loading={commentMutation.isPending}
                    onClick={() => commentMutation.mutate()}
                  >
                    Add Comment
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">History</Text>
                {historyRows.length === 0 ? (
                  <Box padding="400">
                    <Text as="p" tone="subdued" alignment="center">
                      No history recorded
                    </Text>
                  </Box>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                    headings={['Date', 'Change', 'From', 'To', 'Notes', 'By']}
                    rows={historyRows}
                  />
                )}
              </BlockStack>
            </Card>

            {extensionHistory && extensionHistory.length > 0 && (
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Extension History</Text>
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'text', 'text', 'text']}
                    headings={['Date', 'Weeks', 'New End Date', 'Charge', 'Notes']}
                    rows={extensionHistory.map((ext: any) => [
                      new Date(ext.createdAt).toLocaleDateString(),
                      ext.additionalWeeks,
                      ext.newEndDate ? new Date(ext.newEndDate).toLocaleDateString() : '-',
                      `$${Number(ext.chargeAmount || 0).toFixed(2)}`,
                      ext.notes || '-',
                    ])}
                  />
                </BlockStack>
              </Card>
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>

      {/* Change Address Modal */}
      <Modal
        open={replacementOrderModalOpen}
        onClose={() => setReplacementOrderModalOpen(false)}
        title="Create replacement order"
        primaryAction={{
          content: 'Create $0 Replacement Order',
          onAction: () => replacementOrderMutation.mutate(),
          loading: replacementOrderMutation.isPending,
          disabled: !replacementReason.trim(),
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setReplacementOrderModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" tone="subdued">
              Creates a $0 order with the same equipment, wrap, and shipping address.
              It enters the normal dispatch queue — the warehouse assigns the new
              serial and generates both the outbound label and the return label for
              the faulty unit. This rental keeps running unchanged.
            </Text>
            <TextField
              label="Reason for replacement"
              value={replacementReason}
              onChange={setReplacementReason}
              multiline={3}
              autoComplete="off"
              requiredIndicator
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={issueModalOpen}
        onClose={() => setIssueModalOpen(false)}
        title="Report an equipment issue"
        primaryAction={{
          content: 'Flag Issue',
          onAction: () => issueMutation.mutate(),
          loading: issueMutation.isPending,
          disabled: !issueNotes.trim(),
          destructive: true,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setIssueModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" tone="subdued">
              The flag stays on this order and is shown as a warning when the unit is
              scanned back, so QC knows to test it.
            </Text>
            <TextField
              label="What's wrong?"
              value={issueNotes}
              onChange={setIssueNotes}
              multiline={3}
              autoComplete="off"
              requiredIndicator
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        title="Change Shipping Address"
        primaryAction={{
          content: 'Save',
          onAction: handleSaveAddress,
          loading: updateAddressMutation.isPending,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setAddressModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {addressError && (
              <Banner tone="critical">
                <Text as="p">{addressError}</Text>
              </Banner>
            )}
            <Select
              label="Ship to"
              options={[
                { label: 'Clinic', value: 'clinic' },
                { label: 'Patient', value: 'patient' },
                { label: 'Custom Address (e.g. UPS store)', value: 'custom' },
              ]}
              value={addressType}
              onChange={(value) => setAddressType(value)}
            />
            {addressType === 'patient' && (
              (rental as any).patient ? (
                <Banner tone="info">
                  <p>
                    Ships to this order's patient:{' '}
                    <strong>
                      {(rental as any).patient.firstName} {(rental as any).patient.lastName}
                    </strong>
                    {(rental as any).patient.streetAddress
                      ? ` — ${(rental as any).patient.streetAddress}, ${(rental as any).patient.city} ${(rental as any).patient.postalCode}`
                      : ' (no address on file — add one on the patient profile first)'}
                  </p>
                </Banner>
              ) : (
                <Banner tone="warning">
                  <p>This order has no linked patient — use Clinic or a Custom address.</p>
                </Banner>
              )
            )}
            {addressType === 'custom' && (
              <FormLayout>
                <FormLayout.Group>
                  <TextField
                    label="Recipient Name"
                    value={customAddr.name}
                    onChange={(v) => setCustomAddr((p) => ({ ...p, name: v }))}
                    autoComplete="off"
                  />
                  <TextField
                    label="Company / Location"
                    value={customAddr.company}
                    onChange={(v) => setCustomAddr((p) => ({ ...p, company: v }))}
                    placeholder="e.g. The UPS Store #123"
                    autoComplete="off"
                  />
                </FormLayout.Group>
                <TextField
                  label="Street Address"
                  value={customAddr.address1}
                  onChange={(v) => setCustomAddr((p) => ({ ...p, address1: v }))}
                  autoComplete="off"
                />
                <TextField
                  label="Apt / Suite / Unit (optional)"
                  value={customAddr.address2}
                  onChange={(v) => setCustomAddr((p) => ({ ...p, address2: v }))}
                  autoComplete="off"
                />
                <FormLayout.Group>
                  <TextField
                    label="City"
                    value={customAddr.city}
                    onChange={(v) => setCustomAddr((p) => ({ ...p, city: v }))}
                    autoComplete="off"
                  />
                  <TextField
                    label="Province"
                    value={customAddr.province}
                    onChange={(v) => setCustomAddr((p) => ({ ...p, province: v }))}
                    placeholder="ON"
                    autoComplete="off"
                  />
                  <TextField
                    label="Postal Code"
                    value={customAddr.postalCode}
                    onChange={(v) => setCustomAddr((p) => ({ ...p, postalCode: v }))}
                    autoComplete="off"
                  />
                </FormLayout.Group>
                <TextField
                  label="Phone (optional)"
                  type="tel"
                  value={customAddr.phone}
                  onChange={(v) => setCustomAddr((p) => ({ ...p, phone: v }))}
                  autoComplete="off"
                />
              </FormLayout>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Edit Contact Details — order copy, and optionally the patient record */}
      <Modal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        title="Edit Contact Details"
        primaryAction={{
          content: 'Save',
          onAction: () => contactMutation.mutate(),
          loading: contactMutation.isPending,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setContactModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {contactError && (
              <Banner tone="critical" onDismiss={() => setContactError(null)}>
                <p>{contactError}</p>
              </Banner>
            )}
            <TextField
              label="Email"
              type="email"
              value={contactEmail}
              onChange={setContactEmail}
              autoComplete="off"
            />
            <TextField
              label="Phone"
              type="tel"
              value={contactPhone}
              onChange={setContactPhone}
              autoComplete="off"
            />
            <TextField
              label="Patient phone"
              type="tel"
              value={contactPatientPhone}
              onChange={setContactPatientPhone}
              autoComplete="off"
              helpText="Only if the patient's number differs from the contact number above."
            />
            {rental.patientId ? (
              <Checkbox
                label="Also update the patient's record"
                checked={contactUpdateProfile}
                onChange={setContactUpdateProfile}
                helpText={
                  contactUpdateProfile
                    ? 'The correction applies to this order and to the patient, so future orders use it too.'
                    : 'The correction applies to this order only — the patient record keeps its current details.'
                }
              />
            ) : (
              <Text as="p" tone="subdued" variant="bodySm">
                This order has no patient record linked, so the change applies to the order
                only.
              </Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Change Serial — correcting a mis-pick before the order ships */}
      <Modal
        open={changeSerialModalOpen}
        onClose={() => setChangeSerialModalOpen(false)}
        title="Change Serial"
        primaryAction={{
          content: 'Change serial',
          onAction: () => changeSerialMutation.mutate(),
          loading: changeSerialMutation.isPending,
          disabled: !changeNewSerial,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setChangeSerialModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {changeSerialError && (
              <Banner tone="critical" onDismiss={() => setChangeSerialError(null)}>
                <p>{changeSerialError}</p>
              </Banner>
            )}
            <Text as="p">
              Currently assigned: <strong>{changeOldSerial}</strong>
            </Text>
            <SearchableSelect
              label="New serial"
              value={changeNewSerial}
              selectedLabel={changeNewSerial}
              onChange={(value) => setChangeNewSerial(value)}
              onSearch={searchReplacementSerials}
              placeholder="Type or scan a serial…"
              helpText="Only units of the same product that are ready and unassigned."
            />
            <TextField
              label="Reason (optional)"
              value={changeSerialReason}
              onChange={setChangeSerialReason}
              autoComplete="off"
              placeholder="e.g. wrong unit picked"
            />
            <Text as="p" tone="subdued" variant="bodySm">
              {changeOldSerial} goes back into stock and the new unit is allocated to this
              order. Both are recorded in the order history.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Change End Date — a specific date, as opposed to extending by whole weeks */}
      <Modal
        open={endDateModalOpen}
        onClose={() => setEndDateModalOpen(false)}
        title="Change End Date"
        primaryAction={{
          content: 'Save end date',
          onAction: () => endDateMutation.mutate(newEndDate),
          loading: endDateMutation.isPending,
          disabled: !newEndDate,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setEndDateModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {endDateError && (
              <Banner tone="critical" onDismiss={() => setEndDateError(null)}>
                <p>{endDateError}</p>
              </Banner>
            )}
            <TextField
              label="End date"
              type="date"
              value={newEndDate}
              onChange={setNewEndDate}
              autoComplete="off"
              helpText="Overdue is measured against this date, so moving it forward takes the order off the overdue report and moving it back puts it on."
            />
            <Text as="p" tone="subdued" variant="bodySm">
              The original end date is kept on the order for reference, and the change is
              recorded in the order history. This sets a date directly — use Extend Rental
              instead when you want to add whole weeks and charge for them.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Extend Rental Modal */}
      <Modal
        open={extendModalOpen}
        onClose={() => setExtendModalOpen(false)}
        title="Extend Rental"
        primaryAction={{
          content: 'Extend',
          onAction: () => extendMutation.mutate({ rentalId: id!, weeks: parseInt(extendWeeks, 10), notes: extendNotes || undefined }),
          loading: extendMutation.isPending,
          disabled: extensionPricingLoading,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setExtendModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Select
              label="Extension duration"
              options={[1, 2, 3, 4, 5, 6, 7, 8].map((w) => ({
                label: `${w} week${w > 1 ? 's' : ''}`,
                value: String(w),
              }))}
              value={extendWeeks}
              onChange={setExtendWeeks}
            />
            {extensionPreview && (
              <Box padding="200" background="bg-surface-secondary" borderRadius="200">
                <BlockStack gap="200">
                  <Text variant="headingSm" as="h3">Pricing Preview</Text>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Current end date</Text>
                    <Text as="span">{new Date(extensionPreview.currentEndDate).toLocaleDateString()}</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">New end date</Text>
                    <Text as="span" fontWeight="semibold">{new Date(extensionPreview.newEndDate).toLocaleDateString()}</Text>
                  </InlineStack>
                  <Divider />
                  {extensionPreview.lineItems?.map((item: any, idx: number) => (
                    <InlineStack key={idx} align="space-between">
                      <Text as="span" tone="subdued">{item.productName} x {item.weeks}wk</Text>
                      <Text as="span">${Number(item.lineTotal).toFixed(2)}</Text>
                    </InlineStack>
                  ))}
                  <Divider />
                  <InlineStack align="space-between">
                    <Text as="span" fontWeight="semibold">Total charge</Text>
                    <Text as="span" fontWeight="semibold">${Number(extensionPreview.totalCharge).toFixed(2)}</Text>
                  </InlineStack>
                </BlockStack>
              </Box>
            )}
            <TextField
              label="Notes (optional)"
              value={extendNotes}
              onChange={setExtendNotes}
              multiline={2}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Cancel Order Modal */}
      <Modal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        title="Cancel Order"
        primaryAction={{
          content: 'Cancel Order',
          onAction: () => cancelMutation.mutate(),
          loading: cancelMutation.isPending,
          destructive: true,
        }}
        secondaryActions={[{ content: 'Keep Order', onAction: () => setCancelModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Banner tone="warning">
              <p>Are you sure you want to cancel this order? This action cannot be easily undone.</p>
            </Banner>
            <TextField
              label="Cancellation reason"
              value={cancelNotes}
              onChange={setCancelNotes}
              multiline={2}
              autoComplete="off"
              placeholder="Reason for cancellation..."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Refund Modal */}
      <Modal
        open={refundModalOpen}
        onClose={() => setRefundModalOpen(false)}
        title="Process Refund"
        primaryAction={{
          content: 'Process Refund',
          onAction: () => refundMutation.mutate(),
          loading: refundMutation.isPending,
          destructive: true,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setRefundModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Banner tone="warning">
              <p>Are you sure you want to refund this order? The full amount of ${rental ? Number(rental.totalAmount).toFixed(2) : '0.00'} will be refunded.</p>
            </Banner>
            <TextField
              label="Refund notes"
              value={refundNotes}
              onChange={setRefundNotes}
              multiline={2}
              autoComplete="off"
              placeholder="Reason for refund..."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Partial Refund Modal */}
      <Modal
        open={partialRefundModalOpen}
        onClose={() => setPartialRefundModalOpen(false)}
        title="Partial Refund"
        primaryAction={{
          content: 'Process Refund',
          onAction: () => partialRefundMutation.mutate(),
          loading: partialRefundMutation.isPending,
          disabled: !partialRefundAmount || !partialRefundReason,
          destructive: true,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setPartialRefundModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Banner tone="info">
              <p>Order total: <strong>${rental ? Number(rental.totalAmount).toFixed(2) : '0.00'}</strong>. Enter the amount to refund.</p>
            </Banner>
            <TextField
              label="Refund amount"
              type="number"
              prefix="$"
              value={partialRefundAmount}
              onChange={setPartialRefundAmount}
              autoComplete="off"
              helpText={partialRefundAmount ? `New total will be $${(Number(rental?.totalAmount || 0) - parseFloat(partialRefundAmount || '0')).toFixed(2)}` : undefined}
            />
            <TextField
              label="Reason for refund"
              value={partialRefundReason}
              onChange={setPartialRefundReason}
              multiline={2}
              autoComplete="off"
              placeholder="e.g., Late delivery, equipment issue..."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Convert to Sale Modal */}
      <Modal
        open={convertSaleModalOpen}
        onClose={() => setConvertSaleModalOpen(false)}
        title="Convert Rental to Sale"
        primaryAction={{
          content: 'Convert to Sale',
          onAction: () => convertSaleMutation.mutate(),
          loading: convertSaleMutation.isPending,
          disabled: !salePrice || parseFloat(salePrice) <= 0,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setConvertSaleModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Banner tone="warning">
              <p>This will mark the rental as complete and retire the assigned equipment. The patient keeps the equipment.</p>
            </Banner>
            <TextField
              label="Sale price"
              type="number"
              prefix="$"
              value={salePrice}
              onChange={setSalePrice}
              autoComplete="off"
              helpText="The total purchase price for the equipment"
            />
            <TextField
              label="Notes (optional)"
              value={saleReason}
              onChange={setSaleReason}
              multiline={2}
              autoComplete="off"
              placeholder="Reason for conversion..."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Reassign Modal */}
      <Modal
        open={reassignModalOpen}
        onClose={() => setReassignModalOpen(false)}
        title="Reassign Clinic / Patient"
        primaryAction={{
          content: 'Save',
          onAction: handleSaveReassign,
          loading: reassignMutation.isPending,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setReassignModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {reassignError && (
              <Banner tone="critical">
                <Text as="p">{reassignError}</Text>
              </Banner>
            )}
            <Banner tone="info">
              <p>
                Move this order to a different clinic and/or patient. Useful for
                fixing double-bookings before the order ships.
              </p>
            </Banner>
            <SearchableSelect
              label="Clinic"
              value={reassignClinicId}
              selectedLabel={reassignClinicName}
              onChange={(value, option) => {
                setReassignClinicId(value);
                setReassignClinicName(option?.label ?? '');
                setReassignPatientId('');
              }}
              onSearch={searchReassignClinics}
              emptyOption={{ label: '— No clinic —', value: '' }}
              placeholder="Type a clinic name…"
            />
            <Select
              label="Patient"
              options={[
                { label: '— No patient —', value: '' },
                ...((reassignPatientsData?.data ?? []).map((p: any) => ({
                  label: `${p.firstName} ${p.lastName}${p.email ? ` (${p.email})` : ''}`,
                  value: p.id,
                }))),
              ]}
              value={reassignPatientId}
              onChange={setReassignPatientId}
              disabled={!reassignClinicId}
              helpText={!reassignClinicId ? 'Select a clinic first' : undefined}
            />
            <TextField
              label="Reason (optional)"
              value={reassignReason}
              onChange={setReassignReason}
              multiline={2}
              autoComplete="off"
              placeholder="e.g., Double-booked at original clinic"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Replacement Modal */}
      <Modal
        open={replaceModalOpen}
        onClose={() => setReplaceModalOpen(false)}
        title="Replace Equipment"
        primaryAction={{
          content: 'Replace',
          onAction: () => replaceMutation.mutate({
            rentalItemId: replaceItemId,
            oldSerialNumber: replaceOldSerial,
            newSerialNumber: replaceNewSerial,
            reason: replaceReason || undefined,
          }),
          loading: replaceMutation.isPending,
          disabled: !replaceNewSerial,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setReplaceModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Banner tone="info">
              <p>Replacing serial <strong>{replaceOldSerial}</strong>. The old unit will be sent to QC hold.</p>
            </Banner>
            <TextField
              label="New serial number"
              value={replaceNewSerial}
              onChange={setReplaceNewSerial}
              autoComplete="off"
              placeholder="Enter the replacement serial number"
            />
            <TextField
              label="Reason for replacement"
              value={replaceReason}
              onChange={setReplaceReason}
              multiline={2}
              autoComplete="off"
              placeholder="e.g., Device malfunction, patient complaint..."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
      {/* Add an item to an order that has not shipped yet */}
      <Modal
        open={addItemModalOpen}
        onClose={() => {
          setAddItemModalOpen(false);
          setAddItemSelections([]);
        }}
        title="Add an item to this order"
        primaryAction={{
          content: 'Add and reprice',
          onAction: () => addItemsMutation.mutate(),
          loading: addItemsMutation.isPending,
          disabled: addItemSelections.length === 0,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setAddItemModalOpen(false);
              setAddItemSelections([]);
            },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" tone="subdued">
              The order will be repriced. The shipping label stays as it is, since the
              order has not left the warehouse.
            </Text>
            <AddOnPicker
              addons={addOnProducts ?? []}
              selections={addItemSelections}
              onChange={setAddItemSelections}
              rateType={rental.clinicId ? 'wholesale' : 'retail'}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
